/* eslint-disable jsx-a11y/alt-text */
import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { useRouter } from "next/router";
import { observer } from "mobx-react";
import { FiX } from "react-icons/fi";
import Image from "next/image";
import Bugsnag from "@bugsnag/js";
import { FaInstagram, FaTiktok, FaWhatsapp, FaYoutube } from "react-icons/fa";
import { CircleUserIcon, LinkIcon } from "lucide-react";
import { IconInfo } from "../../components/icons/icon-info";
import { IconWhatsapp } from "../../components/icons/icon-whatsapp";
import { MobileContainer } from "../../components/layouts/app/mobile-container";
import {
  mapProductResponseToProduct,
  Product,
} from "../../stores/product-store/product-store.model";
import { SidebarCategory } from "./components/sidebar-category";
import { ProductLayout, ProductList } from "./components/product-list";
import { DEFAULT_ITEM_PER_PAGE } from "../../utils/constants";
import { BuyProductSheet } from "./components/buy-product-sheet";
import { SortSettingsSheet } from "./components/sort-settings-sheet";
import { SellerProfile } from "../../stores/seller-store/seller-store.model";
import { ProductListSkeleton } from "./components/loading-skeleton";
import { FloatingCart } from "./components/floating-cart";
import { BaseModalDialog } from "../../components/modals/base-modal-dialog";
import { useStores } from "../../stores";
import { ResellerLocationModal } from "./components/reseller-location-modal";
import { normalizePhoneNumber } from "../../utils/number";
import { trackEvents } from "../../utils/analytics";
import { SellerPageRouter } from "./page-router.props";
import { SellerHeader } from "./components/seller-header";
import { isAuthenticated } from "../../utils/cookie";
import { LocationSource } from "./components/reseller-location-modal/context";
import { postMessageToBuyerApp } from "../../utils/post-message";
import { mapToCartItem } from "../../stores/cart-store/cart-store.model";
import { getStaticImage } from "../../utils/image";
import {
  ProductListSortOption,
  UpdateLocationAppMessageBody,
} from "../../utils/interfaces";
import { normalizeUrl } from "../../utils/helper";

import { ImageCarouselComponent, ImageColumn, ImageScroll } from "../../modules/seller/components/image-view-component";
import { VideoComponent } from "./components/video-view-component";


interface SellerModuleProps {
  sellerData: SellerProfile;
  isReseller: boolean;
}

export const SellerModule: React.FC<SellerModuleProps> = observer(
  ({ sellerData, isReseller }) => {
    const {
      productStore,
      sellerStore,
      cartStore,
      coverageAreaStore,
      accountStore,
      orderStore,
    } = useStores();

    const [isFirstLoading, setFirstLoading] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isShowSortSettingsSheet, setShowSortSettingsSheet] = useState<boolean>(false);
    const [buySheetData, setBuySheetData] = useState<Product>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isShowLocationPicker, setShowLocationPicker] = useState<boolean>(false);
    const [isShowContactModal, setShowContactModal] = useState<boolean>(false);
    const [productLayout, setProductLayout] = useState<ProductLayout>(ProductLayout.grid);
    const router: SellerPageRouter = useRouter();

    const queryParams = new URLSearchParams(router.asPath.split("?")[1]);

    const fetchProducts = async () => {
      const {
        seller: sellerUsername,
        fromapp,
        q,
        filter,
        category,
        order_by,
        sort_by,
      } = router.query;
      if (q) setSearchQuery(String(q));
      else setSearchQuery("");

      const params = {
        username: String(sellerUsername),
        offset: 0,
        limit: DEFAULT_ITEM_PER_PAGE,
        ...(coverageAreaStore?.selectedCoverageArea?.id && { coverage_area_id: coverageAreaStore?.selectedCoverageArea?.id }),
        ...(q && { name: String(q) }),
        ...(String(filter)?.toLowerCase() === "promo" && { promo_active: true }),
        ...(String(filter)?.toLowerCase() === "new" && { is_new: true }),
        ...(category && { product_category_id: String(category) }),
        ...(order_by && { order_by: String(order_by) }),
        ...(sort_by && { sort_by: String(sort_by) }),
      };

      setIsLoading(true);

      // for authenticated /pesan (retail or trading buyer)
      if (isAuthenticated() && isReseller) {
        const buyerAccount = await accountStore?.getBuyerAccount();
        const { hasReferralCategory } = buyerAccount || {};
        // trading mode
        if (buyerAccount?.trading === true) {
          await productStore?.getProducts(params);
          setIsLoading(false);
        }
        // retail mode
        else if (coverageAreaStore?.selectedCoverageArea?.id) {
          await productStore?.getResellerProducts({
            ...params,
            ...(fromapp &&
              !hasReferralCategory && { filtered_product_category: true }),
          });
          setIsLoading(false);
        }
      }

      // for unauthenticated MartStore buyer
      else {
        if (isReseller && filter !== "personal" && coverageAreaStore?.selectedCoverageArea?.id) {
          await productStore?.getResellerProducts(params);
          setIsLoading(false);
        }
        if (!isReseller || filter === "personal") {
          await productStore?.getProducts(params);
          setIsLoading(false);
        }
      }
    };

    useEffect(() => {
      // set seller data from serverside to mobx store client
      if (sellerData) sellerStore?.setSellerProfile(sellerData);

      // analytics
      if (sellerData?.isReseller) {
        trackEvents({
          sellerUsername: sellerData?.User?.username,
          eventName: "cm_pesan_landing",
          eventCategory: "order",
          eventLabel: "reseller pesan landing",
          trackers: ["GTM", "AF"],
        });
      }

      const reinitiateAreaParams = async () => {
        const selectedCoverageArea = await coverageAreaStore.getSavedCoverageArea();
        // reiniate coverage area query params
        if (selectedCoverageArea) {
          const { pathname, query } = router;
          const savedCheckoutData = await cartStore.getSavedCheckoutData();
          if (selectedCoverageArea?.locationSource) {
            query.areaId = selectedCoverageArea?.id.replace("CRA-", "");
            query.cityId = String(selectedCoverageArea?.areaDistricts[0]?.cityId);
            query.districtId = String(selectedCoverageArea?.areaDistricts[0]?.districtId);
            if (selectedCoverageArea?.locationSource === LocationSource.map && savedCheckoutData) {
              query.address = savedCheckoutData?.customerAddress?.address;
              query.notes = savedCheckoutData?.customerAddress?.notes;
              query.latitude = String(savedCheckoutData?.customerAddress?.latitude);
              query.longitude = String(savedCheckoutData?.customerAddress?.longitude);
            }
            query.locationSource = selectedCoverageArea?.locationSource;
            router.push({ pathname, query }, undefined, { shallow: true });
          }
        }
      };

      const handleMessageFromBuyerApp = async (event: MessageEvent) => {
        if (typeof event?.data === "string") {
          if (event?.data === "refreshProducts") {
            // refetch in case change area
            reinitiateAreaParams();
          }

          if (event?.data === "refreshInfoCart") {
            cartStore?.initCart();
          }

          if (event?.data?.includes("buyerAppAddToCart")) {
            try {
              const {
                id: productId,
                qty,
                unit: selectedUnit,
              } = JSON.parse(event.data);
              const params = {
                username: sellerData?.User?.username,
                id: productId,
                ...(coverageAreaStore?.selectedCoverageArea?.id && {
                  coverage_area_id: coverageAreaStore?.selectedCoverageArea?.id,
                }),
              };
              // check on regular product first
              let result = await productStore?.getProducts(params, false);
              if (!result?.product?.count) {
                // if not found, check on reseller product
                result = await productStore?.getResellerProduct(params, false);
              }
              if (result?.product?.count) {
                const product = mapProductResponseToProduct(
                  result?.product?.rows[0]
                );
                const unit = product.productUnits.find(
                  (unit) => unit.unit === selectedUnit
                );
                const cartItem = mapToCartItem(product, unit, qty);
                cartStore?.addItemToCart(cartItem);

                if (cartItem && sellerStore?.sellerProfile?.sellerJopan) {
                  trackEvents({
                    sellerUsername: sellerStore?.sellerProfile?.User?.username,
                    eventName: "produk_atc",
                    eventCategory: "order",
                    eventLabel: "add to cart",
                    eventValue: { item: cartItem, counter: qty },
                    trackers: ["GTM", "AF", "ME"],
                  });
                }
              }
            } catch (e) {
              console.log(e);
              Bugsnag.notify(e);
            }
          }

          if (event?.data?.includes("buyerAppRepeatOrder")) {
            try {
              const trxNumber = event?.data?.replace(
                "buyerAppRepeatOrder-",
                ""
              );
              const orderDetail = await orderStore?.getOrderDetail(trxNumber);
              await orderStore.repeatOrder(orderDetail);
              // refetch in case change area
              await reinitiateAreaParams();
            } catch (e) {
              console.log(e);
              Bugsnag.notify(e);
            }
          }

          if (
            typeof event.data === "string" &&
            event.data.includes("productListLocation")
          ) {
            try {
              const parsedData: UpdateLocationAppMessageBody = JSON.parse(
                event.data
              );
              const selectedCoverageArea =
                await coverageAreaStore?.getAndSetCoverageArea({
                  cityId: parsedData?.selectedCity,
                  districtId: parsedData?.selectedDistrict,
                  locationSource:
                    parsedData.locationSource === "MAP"
                      ? LocationSource.map
                      : LocationSource.dropdown,
                });
              if (selectedCoverageArea) {
                const savedCheckoutData =
                  await cartStore?.getSavedCheckoutData();
                await cartStore?.saveCheckoutData({
                  ...savedCheckoutData,
                  customerAddress: {
                    ...(parsedData.address && { address: parsedData.address }),
                    ...(parsedData.location?.latitude && {
                      latitude: parsedData.location.latitude,
                    }),
                    ...(parsedData.location?.longitude && {
                      longitude: parsedData.location.longitude,
                    }),
                    ...(parsedData.state && {
                      stateId: parsedData.selectedProvince,
                    }),
                    ...(parsedData.city && { cityId: parsedData.selectedCity }),
                    ...(parsedData.district && {
                      districtId: parsedData.selectedDistrict,
                    }),
                    subdistrictId: null,
                    ...(parsedData.zipcode && { zipcode: parsedData.zipcode }),
                    ...(parsedData.addressNote && {
                      notes: parsedData.addressNote,
                    }),
                    ...(parsedData.placeId && { placeId: parsedData.placeId }),
                  },
                });
                handleLocationSubmit();
              }
            } catch (e) {
              console.log(e);
              Bugsnag.notify(e);
            }
          }

          if (
            typeof event?.data === "string" &&
            (event.data.includes("productListFilter") ||
              event.data.includes("productListSort"))
          ) {
            try {
              // format = productListFilter-price_desc
              const data = event.data.split("-");
              if (data.length > 1) {
                if (data[1].includes("reset")) {
                  onSubmitSort();
                } else {
                  const sortAndOrder = data[1].split("_");
                  if (sortAndOrder.length > 1) {
                    const sort_by = sortAndOrder[0];
                    const order_by = sortAndOrder[1];
                    onSubmitSort({ sort_by, order_by });
                  }
                }
              }
            } catch (e) {
              console.log(e);
              Bugsnag.notify(e);
            }
          }

          if (
            typeof event.data === "string" &&
            event.data.includes("productListSearch")
          ) {
            try {
              // format = productListSearch-query
              const data = event.data.split("-");
              if (data.length > 1) {
                const searchQuery =
                  data.length === 2
                    ? data[1]
                    : data.slice(1, data.length - 1).join("");
                const params = new URLSearchParams(router.asPath.split("?")[1]);
                params.set("q", searchQuery);
                router.push(
                  {
                    pathname: normalizeUrl(router.asPath),
                    query: params.toString(),
                  },
                  undefined,
                  { shallow: true }
                );
              }
            } catch (e) {
              console.log(e);
              Bugsnag.notify(e);
            }
          }

          if (
            typeof event.data === "string" &&
            event.data.includes("productListResetSearch")
          ) {
            try {
              const params = new URLSearchParams(router.asPath.split("?")[1]);
              if (params.get("q")) params.delete("q");
              router.push(
                {
                  pathname: normalizeUrl(router.asPath),
                  query: params.toString(),
                },
                undefined,
                { shallow: true }
              );
            } catch (e) {
              Bugsnag.notify(e);
            }
          }

          if (
            typeof event.data === "string" &&
            event.data.includes("productListLayout")
          ) {
            try {
              // format = productListLayout-grid or list
              const data = event.data.split("-");
              if (data.length > 1) {
                if (data[1] === "list") setProductLayout(ProductLayout.list);
                else setProductLayout(ProductLayout.grid);
              }
            } catch (e) {
              console.log(e);
              Bugsnag.notify(e);
            }
          }
        }
      };
      window.addEventListener("message", handleMessageFromBuyerApp);
      return () => {
        window.removeEventListener("message", handleMessageFromBuyerApp);
      };

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      async function initReseller() {
        // init coverage area
        const {
          areaId,
          cityId,
          districtId,
          locationSource,
          address,
          latitude,
          longitude,
          notes,
        } = router.query;
        const formattedAddress =
          (address &&
            String(address)
              ?.replace(/([A-Z]+)/g, " $1")
              .trim()) ||
          "";
        const selectedCoverageArea =
          await coverageAreaStore?.getSavedCoverageArea({
            ...(areaId && { coverageAreaId: `CRA-${areaId}` }),
            ...(cityId && { cityId: parseInt(cityId as string) }),
            ...(districtId && { districtId: parseInt(districtId as string) }),
            ...(locationSource && { locationSource: locationSource as string }),
            ...(formattedAddress && { address: formattedAddress }),
            ...(latitude && { latitude: parseFloat(latitude as string) }),
            ...(longitude && { longitude: parseFloat(longitude as string) }),
            ...(notes && { notes: notes as string }),
          });

        const { pathname, query } = router;

        // reiniate coverage area query params
        if (selectedCoverageArea && !locationSource) {
          const savedCheckoutData = await cartStore.getSavedCheckoutData();
          if (selectedCoverageArea?.locationSource) {
            query.areaId = selectedCoverageArea?.id.replace("CRA-", "");
            query.cityId = String(
              selectedCoverageArea?.areaDistricts[0]?.cityId
            );
            query.districtId = String(
              selectedCoverageArea?.areaDistricts[0]?.districtId
            );
            if (
              selectedCoverageArea?.locationSource === LocationSource.map &&
              savedCheckoutData
            ) {
              query.address = savedCheckoutData?.customerAddress?.address;
              query.notes = savedCheckoutData?.customerAddress?.notes;
              query.latitude = String(
                savedCheckoutData?.customerAddress?.latitude
              );
              query.longitude = String(
                savedCheckoutData?.customerAddress?.longitude
              );
            }
            query.locationSource = selectedCoverageArea?.locationSource;
            router.push({ pathname, query }, undefined, { shallow: true });
          }
        }

        // show or hide location picker
        if (!selectedCoverageArea || coverageAreaStore.isLocationPickerOpen) {
          if (!query.fromapp) {
            setShowLocationPicker(true);
            coverageAreaStore.setLocationPickerOpen(false);
          }
        }

        // cart after coverageAreaId
        cartStore.initCart();
        fetchProducts();
      }

      async function init() {
        if (isReseller) await initReseller();
        else {
          cartStore.initCart();
          await fetchProducts();
        }
        if (isFirstLoading) setFirstLoading(false);
      }

      if (sellerData?.isDomainActive) {
        init();
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query]);

    const handleLocationSubmit = useCallback(() => {
      const params = new URLSearchParams();

      if (coverageAreaStore?.selectedCoverageArea?.areaDistricts[0]) {
        const locationSource =
          coverageAreaStore?.selectedCoverageArea?.locationSource;
        const selectedCity = coverageAreaStore.selectedCity;
        const selectedDistrict = coverageAreaStore.selectedDistrict;
        params.set(
          "areaId",
          coverageAreaStore.selectedCoverageArea?.id.replace("CRA-", "")
        );
        if (selectedCity?.id && selectedDistrict?.id) {
          params.set("cityId", String(selectedCity?.id));
          params.set("districtId", String(selectedDistrict?.id));
        }
        if (locationSource === LocationSource.map) {
          const { customerAddress } = cartStore.savedCheckoutData;
          params.set("address", customerAddress?.address);
          if (customerAddress?.latitude)
            params.set("latitude", String(customerAddress?.latitude));
          if (customerAddress?.longitude)
            params.set("longitude", String(customerAddress?.longitude));
          if (customerAddress?.notes)
            params.set("notes", String(customerAddress?.notes));
        }
        params.set("locationSource", locationSource);

        // reset selected payment method on checkout data
        if (cartStore?.savedCheckoutData?.paymentMethod) {
          cartStore?.saveCheckoutData({
            ...cartStore?.savedCheckoutData,
            paymentMethod: null,
          });
        }

        // analytics
        if (locationSource === LocationSource.dropdown) {
          trackEvents({
            sellerUsername: sellerStore?.sellerProfile?.User?.username,
            eventName: "produk_simpan_kecamatan",
            eventCategory: "pesan",
            eventLabel: "select location",
            eventValue: {
              coverageAreaId: coverageAreaStore?.selectedCoverageArea?.id,
              cityName: selectedCity?.name,
              districtName: selectedDistrict?.name,
            },
            moengageContextId: coverageAreaStore?.selectedCoverageArea?.id,
            trackers: ["GTM", "AF", "ME"],
          });
        }
        if (locationSource === LocationSource.map) {
          trackEvents({
            sellerUsername: sellerStore?.sellerProfile?.User?.username,
            eventName: "produk_simpan_alamat",
            eventCategory: "pesan",
            eventLabel: "select location",
            eventValue: {
              coverageAreaId: coverageAreaStore?.selectedCoverageArea?.id,
              cityName: selectedCity?.name,
              districtName: selectedDistrict?.name,
            },
            moengageContextId: coverageAreaStore?.selectedCoverageArea?.id,
            trackers: ["GTM", "AF", "ME"],
          });
        }
        trackEvents({
          sellerUsername: sellerStore?.sellerProfile?.User?.username,
          eventName: "cm_pesan_select_location",
          eventCategory: "pesan",
          eventLabel: "select location",
          trackers: ["GTM", "AF"],
        });
        trackEvents({
          sellerUsername: sellerStore?.sellerProfile?.User?.username,
          eventName: "produk_lihat_semua",
          eventCategory: "pesan",
          eventLabel: "see product after choose address",
          trackers: ["GTM", "AF", "ME"],
        });
      }

      if (router.query.fromapp) params.set("fromapp", "true");

      router.push(
        {
          pathname: normalizeUrl(router.asPath),
          query: params.toString(),
        },
        undefined,
        { shallow: true }
      );

      // refetch cart by coverage area
      cartStore?.initCart();

      if (router.query.fromapp) postMessageToBuyerApp("cart-product-change");
    }, [
      cartStore,
      coverageAreaStore.selectedCity,
      coverageAreaStore.selectedCoverageArea,
      coverageAreaStore.selectedDistrict,
      router,
      sellerStore?.sellerProfile?.User?.username,
    ]);

    const handleGoToContactWA = () => {
      const textMessage = `Hai%20${process.env.NEXT_PUBLIC_BRAND_NAME}!`;
      if (router.query.fromapp) {
        postMessageToBuyerApp(
          "store-whatsapp",
          normalizePhoneNumber(
            coverageAreaStore?.selectedCoverageArea?.phone ||
            sellerData?.User?.phone ||
            normalizePhoneNumber(process.env.NEXT_PUBLIC_URL_WA)
          )
        );
      } else {
        window.open(
          `https://wa.me/${normalizePhoneNumber(
            coverageAreaStore?.selectedCoverageArea?.phone ||
            sellerData?.User?.phone ||
            normalizePhoneNumber(process.env.NEXT_PUBLIC_URL_WA)
          )}?text=${textMessage}`
        );
      }
      if (sellerData?.sellerJopan) {
        trackEvents({
          sellerUsername: sellerData?.User?.username,
          eventName: "produk_wa",
          eventCategory: "product",
          eventLabel: "wa contact",
          eventValue: { merchantInfo: sellerData?.User?.name },
          trackers: ["GTM", "AF", "ME"],
        });
      }
    };

    const handleGoToStoreInfo = () => {
      if (!sellerData) return;
      setShowContactModal(false);
      if (router.query.fromapp) {
        postMessageToBuyerApp("store-info");
      } else {
        router.push(`/${router.query.seller}/info`);
      }
      if (sellerData?.sellerJopan) {
        trackEvents({
          sellerUsername: sellerData?.User?.username,
          eventName: "produk_info",
          eventCategory: "product",
          eventLabel: "product info",
          eventValue: { merchantName: sellerData?.User?.username },
          trackers: ["GTM", "AF", "ME"],
        });
      }
    };

    const onProductItemClickBuy = useCallback(
      (product: Product) => {
        if (!router?.query?.fromapp) {
          setBuySheetData(product);
        } else {
          postMessageToBuyerApp("product-list-atc", product.id);
        }
      },
      [router?.query?.fromapp]
    );

    const onSubmitSort = useCallback(
      (selectedSortOption?: ProductListSortOption) => {
        const params = new URLSearchParams(router.asPath.split("?")[1]);
        if (selectedSortOption) {
          params.set("order_by", selectedSortOption.order_by);
          params.set("sort_by", selectedSortOption.sort_by);
        } else {
          params.delete("order_by");
          params.delete("sort_by");
        }
        // to keep on the app mode
        if (router.query.fromapp) params.set("fromapp", "true");
        router.push(
          {
            pathname: normalizeUrl(router.asPath),
            query: params.toString(),
          },
          undefined,
          { shallow: true }
        );
        setShowSortSettingsSheet(false);
        if (sellerData?.sellerJopan) {
          trackEvents({
            sellerUsername: sellerData?.User?.name,
            eventName: "pesanan_sort",
            eventCategory: "order",
            eventLabel: "sort order",
            trackers: ["GTM", "AF", "ME"],
          });
        }
      },
      [router, sellerData?.User?.name, sellerData?.sellerJopan]
    );

    const renderLocationPickerModal = () => {
      return (
        isReseller && (
          <ResellerLocationModal
            isOpen={isShowLocationPicker}
            onClose={() => setShowLocationPicker(false)}
            handleSubmitLocation={handleLocationSubmit}
          />
        )
      );
    };

    const renderContactModal = () => {
      return (
        <BaseModalDialog
          isOpen={isShowContactModal}
          onClose={() => setShowContactModal(false)}
          className="w-full max-w-[440px]"
        >
          <div className="flex items-center justify-between mb-6">
            <span className="font-bold text-md">Hubungi Toko</span>
            <FiX
              size={20}
              onClick={() => setShowContactModal(false)}
              className="cursor-pointer"
              id="seller-contact-dialog-close-btn"
              data-testid="seller-contact-dialog-close-btn"
            />
          </div>
          <a
            type="button"
            onClick={handleGoToContactWA}
            className="flex items-center justify-center w-full py-3 mb-4 rounded border shadow-light transition hover:bg-gray-50 font-bold cursor-pointer"
            id="seller-contact-dialog-wa-btn"
            data-testid="seller-contact-dialog-wa-btn"
          >
            <div className="flex items-center justify-center">
              <IconWhatsapp className="mr-2 text-green" />
              <span>WhatsApp</span>
            </div>
          </a>
          <a
            type="button"
            onClick={handleGoToStoreInfo}
            className={classNames(
              "flex justify-center w-full py-3 rounded shadow-light border transition hover:bg-gray-50 font-bold",
              {
                "cursor-pointer": sellerData,
                "cursor-not-allowed bg-gray-50": !sellerData,
              }
            )}
            id="seller-contact-dialog-info-btn"
            data-testid="seller-contact-dialog-info-btn"
          >
            <div className="flex items-center justify-center">
              <IconInfo className="mr-2" />
              <span>Info Toko</span>
            </div>
          </a>
        </BaseModalDialog>
      );
    };

    console.log(sellerData)

    return (
      <MobileContainer bottomNavigation sellerData={sellerData}>

        <div className="flex flex-col">
          {queryParams.size === 0 &&
            <div className="p-4 relative">
              <div className="flex flex-col items-center">
                {sellerData?.landing_setting && sellerData?.landing_setting.sellerInfo ? <img src={sellerData?.landing_setting.sellerInfo.profilePicture} className="w-[5rem] aspect-square rounded-full border" /> : <CircleUserIcon size={64} />}
                {sellerData?.landing_setting && sellerData?.landing_setting.sellerInfo ? <div className="mt-2 text-xl font-bold">{sellerData?.landing_setting.sellerInfo.name}</div> : ''}
                {sellerData?.landing_setting && sellerData?.landing_setting.sellerInfo ? <div>{sellerData?.landing_setting.sellerInfo.description}</div> : ''}
                {sellerData?.landing_setting && sellerData?.landing_setting.sellerInfo ?
                  <div className="mt-2 flex flex-row gap-4">
                    {sellerData?.landing_setting.sellerInfo.socials?.map((soc) => (
                      <a key={soc.key} href={soc.value} target="__blank">
                        {soc.type === 'whatsapp' && <div><FaWhatsapp size={20} /></div>}
                        {soc.type === 'instagram' && <div><FaInstagram size={20} /></div>}
                        {soc.type === 'tiktok' && <div><FaTiktok size={20} /></div>}
                        {soc.type === 'youtube' && <div><FaYoutube size={20} /></div>}
                        {soc.type === 'website' && <div><LinkIcon size={20} /></div>}
                      </a>
                    ))}
                  </div> : ''
                }
              </div>
              <div className="mt-4 w-full border border-gray-400" />
            </div>
          }

          {queryParams.size === 0 && sellerData?.landing_setting && sellerData?.landing_setting.components && sellerData?.landing_setting.components?.filter(comp => comp.active !== false).map((comp, index) => {
            if (comp.type === 'image') {
              if (comp.variant === 'carousel-wide') {
                return <ImageCarouselComponent key={index} data={comp.imageConfig?.data ?? []} wide links={comp.imageConfig?.links} />
              } else if (comp.variant === 'carousel-square') {
                return <ImageCarouselComponent key={index} data={comp.imageConfig?.data ?? []} links={comp.imageConfig?.links} />
              } else if (comp.variant === 'column-1') {
                return <ImageColumn key={index} data={comp.imageConfig?.data ?? []} column={1} wide={comp.imageConfig?.wide} links={comp.imageConfig?.links} />
              } else if (comp.variant === 'column-2') {
                return <ImageColumn key={index} data={comp.imageConfig?.data ?? []} column={2} links={comp.imageConfig?.links} />
              } else if (comp.variant === 'column-3') {
                return <ImageColumn key={index} data={comp.imageConfig?.data ?? []} column={3} links={comp.imageConfig?.links} />
              } else if (comp.variant === 'scroll') {
                return <ImageScroll key={index} data={comp.imageConfig?.data ?? []} links={comp.imageConfig?.links} />
              } else {
                return <div key={index} />
              }
            } else if (comp.type === 'video') {
              return <VideoComponent key={index} data={comp.videoConfig ?? null} />
            } else {
              return <div key={index} />
            }
          })
          }
        </div>

        <div>
          {!router?.query?.fromapp && (
            <SellerHeader
              isReseller={isReseller}
              sellerData={sellerData}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              setShowLocationPicker={setShowLocationPicker}
              setShowContactModal={setShowContactModal}
              setShowSortSettingsSheet={setShowSortSettingsSheet}
              productLayout={productLayout}
              setProductLayout={setProductLayout}
              isStoreClosed={!sellerData?.isDomainActive}
            />
          )}

          {sellerData?.isDomainActive && (
            <>
              <div className="flex h-full px-2"
                style={{
                  backgroundColor: sellerData?.landing_setting?.appearanceSetting?.mainBgColor || '#FFFFFF',
                  color: sellerData?.landing_setting?.appearanceSetting?.mainTextColor || '#000000'
                }}>
                <SidebarCategory
                  isReseller={isReseller}
                  sellerData={sellerData}
                />
                {isFirstLoading || isLoading ? (
                  <ProductListSkeleton mode={productLayout} />
                ) : (
                  !isFirstLoading && (
                    <ProductList
                      isReseller={isReseller}
                      sellerData={sellerData}
                      mode={productLayout}
                      products={productStore.allProducts}
                      onClickBuy={onProductItemClickBuy}
                    />
                  )
                )}
              </div>
              {renderLocationPickerModal()}
              {renderContactModal()}
              <FloatingCart
                productCount={cartStore?.cartItems?.length}
                totalOrderPrice={cartStore?.cartSummary?.totalOrderPrice}
              />
              <SortSettingsSheet
                visible={isShowSortSettingsSheet}
                onDismiss={() => setShowSortSettingsSheet(false)}
                onSubmitSort={onSubmitSort}
              />
              <BuyProductSheet
                product={buySheetData}
                onDismiss={() => setBuySheetData(null)}
              />
            </>
          )}

          {!sellerData?.isDomainActive && (
            <div className="mt-20 flex flex-col self-center items-center w-72 text-center">
              <Image
                src={getStaticImage("empty-product.svg")}
                width="200"
                height="200"
                alt="Empty Cart"
              />
              <h3 className="font-bold text-xl mt-8 mb-4">Toko Tutup</h3>
              <p className="text-gray">
                Toko ini sedang tidak beroperasi, silakan hubungi penjual
              </p>
              {renderContactModal()}
            </div>
          )}
        </div>
      </MobileContainer>
    );
  }
);
